import { useQuery } from '@tanstack/react-query';

import { API_BASE } from '../../lib/api';
import type { Site } from '../../lib/types';
import { Badge, Card, CopyButton, LoadError, Skeleton } from '../../lib/ui';
import { Hilfe } from '../help/Help';
import { t } from '../../lib/i18n';

/**
 * The cookie declaration, ready to paste into a privacy policy.
 *
 * German supervisory authorities expect the policy to name which cookies a site sets, how
 * long they live and who receives the data. Keeping that list accurate by hand is
 * impossible — it changes with every tag someone adds — which is why every competitor
 * generates it. We already hold all of it.
 *
 * Two ways out, deliberately: a public JSON endpoint for anyone who wants to render it
 * themselves, and static HTML to copy for everyone else. No embed script, because a
 * privacy product should not ask a customer to add another third-party script to the page
 * whose scripts it is there to control.
 */
interface DeclVendor {
  name: string;
  retention?: string;
  policy_url?: string;
  cookies?: string[];
}
interface DeclCategory {
  slug: string;
  label: string;
  desc?: string;
  required: boolean;
  vendors: DeclVendor[];
}
interface Decl {
  site: string;
  config_version: string;
  categories: DeclCategory[];
}

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Table markup with no classes and no styles: it inherits the customer's own typography. */
function toHtml(d: Decl): string {
  const rows = d.categories.flatMap((c) =>
    c.vendors.map(
      (v) =>
        `    <tr>\n` +
        `      <td>${esc(c.label)}</td>\n` +
        `      <td>${esc(v.name)}</td>\n` +
        `      <td>${esc((v.cookies ?? []).join(', '))}</td>\n` +
        `      <td>${esc(v.retention ?? '')}</td>\n` +
        `      <td>${v.policy_url ? `<a href="${esc(v.policy_url)}" rel="noopener">${esc(t('Datenschutzerklärung'))}</a>` : ''}</td>\n` +
        `    </tr>`,
    ),
  );
  return (
    `<table>\n  <caption>${esc(t('Eingesetzte Cookies und Dienste auf {site}', { site: d.site }))}</caption>\n` +
    `  <thead>\n    <tr>\n      <th>${esc(t('Kategorie'))}</th><th>${esc(t('Anbieter'))}</th>` +
    `<th>${esc(t('Cookies'))}</th>` +
    `<th>${esc(t('Speicherdauer'))}</th><th>${esc(t('Details'))}</th>\n    </tr>\n  </thead>\n  <tbody>\n` +
    rows.join('\n') +
    `\n  </tbody>\n</table>`
  );
}

export function DeclarationCard({ site }: { site: Site }) {
  const url = `${API_BASE}/v1/c/${site.site_key}/cookies.json`;
  const decl = useQuery({
    queryKey: ['declaration', site.site_key],
    // Public, so no token — the same URL a customer would put in their own page.
    queryFn: async (): Promise<Decl | null> => {
      const res = await fetch(url);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as Decl;
    },
  });

  const d = decl.data;
  const count = d?.categories.reduce((n, c) => n + c.vendors.length, 0) ?? 0;

  return (
    <Card
      title={t('Cookie-Erklärung')}
      count={count || undefined}
      actions={
        <span className="flex items-center gap-1">
          {d && <CopyButton value={toHtml(d)} label={t('Als HTML kopieren')} />}
          <Hilfe to="cookie-erklaerung" />
        </span>
      }
    >
      <p className="mb-3 text-xs text-[var(--color-muted)]">
        {t('Für Ihre Datenschutzerklärung. Sie wird aus den veröffentlichten Anbietern erzeugt und ändert sich mit jeder Veröffentlichung — was von Hand gepflegte Listen nie tun.')}
      </p>

      {decl.isPending && <Skeleton rows={4} />}
      {decl.isError && <LoadError what={t('Die Cookie-Erklärung')} onRetry={() => void decl.refetch()} />}

      {decl.isSuccess && !d && (
        <p className="text-[13px] text-[var(--color-muted)]">
          {/* Not an empty table: "we set no cookies" is a statement, and it would be false. */}
          {t('Noch nichts veröffentlicht — es gibt daher noch nichts zu erklären.')}
        </p>
      )}

      {d && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-[var(--color-line)] text-left">
                  <th className="eyebrow py-1 pr-3 font-normal">{t('Anbieter')}</th>
                  <th className="eyebrow py-1 pr-3 font-normal">{t('Kategorie')}</th>
                  <th className="eyebrow py-1 pr-3 font-normal">{t('Cookies')}</th>
                  <th className="eyebrow py-1 font-normal">{t('Dauer')}</th>
                </tr>
              </thead>
              <tbody>
                {d.categories.flatMap((c) =>
                  c.vendors.map((v) => (
                    <tr key={`${c.slug}:${v.name}`} className="border-b border-[var(--color-line)]/60">
                      <td className="py-1.5 pr-3">
                        {v.policy_url ? (
                          <a className="hover:underline" href={v.policy_url} target="_blank" rel="noreferrer">
                            {v.name}
                          </a>
                        ) : v.name}
                      </td>
                      <td className="py-1.5 pr-3">
                        <Badge tone={c.required ? 'neutral' : 'neutral'}>{c.label}</Badge>
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-[11px] text-[var(--color-muted)]">
                        {(v.cookies ?? []).join(', ') || '—'}
                      </td>
                      <td className="py-1.5 text-[var(--color-muted)]">{v.retention || '—'}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-[var(--color-muted)]">
            {t('Wer sie lieber selbst rendert, holt dieselben Daten hier:')}{' '}
            <a className="font-mono hover:underline" href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          </p>
        </>
      )}
    </Card>
  );
}
